/* eslint-disable arrow-body-style */
import MetricsCardWrap from '@/pages/dashboard/components/MetricsCardWrap';
import CardBox from '@/pages/dashboard/components/CardBox';
import { CommonBaseTable } from '@home/components/CommonBaseTable';
import type { ArtColumn } from 'ali-react-table';
import { handleDownload } from '@/pages/dashboard/utils/utils';
import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Switch, Radio } from 'tea-component';
import { useAiSummary, useLinkPath, useLocal } from '@/pages/dashboard/utils/customHook';
import { getAlignmentAiDataKeyField } from '@/pages/dashboard/utils/aiAlignmentUtils';
import Loading from '@/pages/dashboard/components/Loading';
import FormatHtmlTextToReact from '@home/components/FormatHtmlTextToReact';
import { getName } from '@home/utils/common';
import classNames from 'classnames';
import { DownloadButton } from '@/pages/dashboard/components/Plugin/DownloadButton';
import '@/pages/dashboard/components/Plugin/index.scss';
import LineChart from '@/pages/dashboard/components/Chart/components/LineChart';
import { getEchartTooltipString } from '@/pages/dashboard/components/EchartsTooltip';
import type { ITableOption } from '@/pages/dashboard/components/EchartsTooltip/type';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import type { IFormatRule } from '@home/utils/dbDataFormat/_type';
import Abnormal from '@home/components/Abnormal';
import {
  addChartLabel,
  applyEchartsOptionFontSizeScale,
  isEmptyLineDataNeighbor,
  resolveFullscreenFontScales,
  xAxisHighLight,
} from '../Chart/_config/plug';
import './index.scss';
import dateTimeFormat from '@home/utils/dbDataFormat/dateTimeFormat';
import { history, useIntl, useParams } from 'umi';
import EventWindows from '@op/pages/Overview/KeyMetrics/MentionsSentiment/MetricsChart/Charts/BarAndLine/EventWindows';
import { ChartRef } from '../Chart/_config/interface';
import { debounce, throttle } from 'lodash-es';
import SvgIcon from '@home/components/SvgIcon';
import {
  applyEventTypeCollapseToEventDataMap,
  applyEventTypeDisplayOrderToEventDataMap,
  applyEventTypeBarColorMapToEventDataMap,
  buildEventTypeTagStyleMapFromBarColorMap,
  eventAxisValueToQueryDate,
  extractKpiXAxisBubbleLabelColors,
  filterEventDataMapDropStartBeforeFirstAxisTick,
  filterRawEventListDropStartBeforeFirstAxisTick,
  formatEventDataSource,
  resolveEventTypeCollapseConfig,
} from './utils';
import EventDetailsPop from '@/pages/dashboard/pages/eventAnalysis/EventDetailsPop';
import {
  buildChartRenderEventList,
  buildEventDetailsPopAlignedDays,
  buildRawEventListForModalDataKey,
  groupRawEventListByTypeForModal,
  KPI_DEFAULT_EVENT_TYPE_PRIORITY,
  resolveKpiEventUi,
} from './kpiEventUiUtils';
import { applyChartEventWindowsPipelineToEventDataMap } from './chartEventWindowsPipeline';
import { useEventMetricTrendKpiEventUi } from './useEventMetricTrendKpiEventUi';
import { KpiOverviewEventInjectContext } from '@/pages/dashboard/pages/overview/kpiOverview/contexts/KpiOverviewEventInjectContext';
import { useEventMetricTrendKpiEventDetailsPop } from './useEventMetricTrendKpiEventDetailsPop';
import EventMetricTrendEventSettingModal from './EventMetricTrendEventSettingModal';
import { useEventMetricTrendTable } from './useEventMetricTrendTable';
import { resolveKpiMetricTrendDownloadColumns } from '@/pages/dashboard/pages/overview/kpiOverview/components/KpiEventMetricTrendService/_utils';
import { applyLegendDimensionIcons } from './legendDimensionIcons';
import ViewportFullscreenTip from '@/pages/dashboard/components/ViewportFullscreenTip';
import {
  EventMetricTrendProps,
  RadioOption,
  SeriesConfig,
  TooltipParams,
  TooltipDataItem,
  EChartsOption,
  EChartsSeries,
  EventDataMap,
  DownloadColumn,
  SeriesDataItem,
  PropsEventData,
  ChartData,
  ExperimentTypeFixedLabelParams,
  ApiDataWrapper,
  type TEventStartTimeAxisClipMode,
} from './types';

/** 全屏时折线高度 = 视口高 − 该值（全屏 padding、标题区、CardBody 等）；调小则折线更高、底部留白更少 */
const FULLSCREEN_EVENT_METRIC_TREND_CHROME_RESERVE = 160;

const EventMetricTrend: React.FC<EventMetricTrendProps> = (props) => {
  const {
    titleCn,
    titleEn,
    children,
    className,
    style,
    subTitle,
    subTitleCn,
    data,
    seriesConfig: rawSeriesConfig,
    doubleBarChartConfig,
    apiDataKey = 'trendData',
    eventKey = 'eventData',
    chartConfig,
    defaultShowEvent = true,
    showEventSwitch = true,
    radioConfig,
    link,
    downloadConfig,
    yAxisMin = { enabled: true, minRatio: 0.2 },
    // HEAD: contentHeader & forceNoData
    contentHeader,
    forceNoData: forceNoDataProp,
    // feat: 扩展功能 props
    hideControls = false,
    controlledShowEvent,
    controlledRadioValue,
    aiTag,
    aiTagMap,
    enableAlignmentAiDataKey = false,
    alignmentDataKeyMap,
    showTableView = false,
    tableViewHeight = 375,
    abnormalHeight: abnormalHeightProp,
    trendChartHeight: trendChartHeightProp,
    viewportFullscreen,
    ...restProps
  } = props;

  const { formatMessage } = useIntl();
  const kpiUi = resolveKpiEventUi(chartConfig?.kpiEventUi);
  const kpiOverviewEventInject = useContext(KpiOverviewEventInjectContext);
  const kpiShared = kpiOverviewEventInject?.kpiSharedEventSetting;
  /** 总览筛选注入时，与开启 kpiEventUi 的卡共用「按筛选 bubble + 轴/气泡」数据管线 */
  const kpiFilterBubblePipeline = kpiUi.enabled || Boolean(kpiShared);
  const effectiveEventTypeCollapseConfig = useMemo(
    () =>
      resolveEventTypeCollapseConfig(chartConfig, {
        useKpiExperimentMatchDefaults: kpiFilterBubblePipeline,
      }),
    [
      kpiFilterBubblePipeline,
      chartConfig?.eventTypeCollapse,
      chartConfig?.chartEventExperimentTooltip,
    ],
  );
  const effectiveShowTableView = Boolean(showTableView) && !hideControls;
  const [viewChartType, setViewChartType] = useState<string>('lineChart');

  /** 事件 start_time 相对 X 轴裁剪；默认仅去掉早于最早刻度的，`within_range` 为双端，`false` 关闭 */
  const eventStartTimeAxisClipResolved: TEventStartTimeAxisClipMode | false =
    useMemo(() => {
      const c = chartConfig?.eventStartTimeAxisClip;
      if (c === false) {
        return false;
      }
      if (c === 'within_range') {
        return 'within_range';
      }
      return 'before_min';
    }, [chartConfig?.eventStartTimeAxisClip]);

  const anomalyScatterConfigs = data?._anomalyScatterConfigs as
    | SeriesConfig[]
    | undefined;
  const seriesConfig = useMemo(
    () =>
      anomalyScatterConfigs?.length
        ? [...(rawSeriesConfig || []), ...anomalyScatterConfigs]
        : rawSeriesConfig,
    [rawSeriesConfig, anomalyScatterConfigs],
  );

  const { isEN, locale } = useLocal();
  const routeParams = useParams() as {
    type?: string;
    gamecode?: string;
  };
  const eventBubbleDetailsNavCfg = chartConfig?.eventBubbleDetailsNav;

  /** 仅配置 eventBubbleDetailsNav 时拼接详情页 path，避免未开跳转时也依赖 type/gamecode */
  const eventDetailsPagePath = useMemo(() => {
    if (!eventBubbleDetailsNavCfg) {
      return '';
    }
    const { type, gamecode } = routeParams;
    if (!type || !gamecode) {
      return '';
    }
    const rel = eventBubbleDetailsNavCfg.path ?? 'eventAnalysis/eventDetails';
    return `/dashboard/${type}/${gamecode}/${rel}`;
  }, [eventBubbleDetailsNavCfg, routeParams]);

  /** EventDetailsPop 「更多」跳转用；与 eventBubbleDetailsNav.path 共享相对路径 */
  const eventDetailsPopPathname = useLinkPath(
    eventBubbleDetailsNavCfg?.path ?? 'eventAnalysis/eventDetails',
  );

  const handleEventBubbleDetailsNavigate = useCallback(
    // item 为事件气泡原始记录，字段由上游业务决定，仅消费 event_type_id / event_type.id
    (item: { event_type_id?: string | number; event_type?: { id?: string | number; }; } | null | undefined, xAxisValue: string) => {
      if (!eventBubbleDetailsNavCfg || !eventDetailsPagePath) {
        return;
      }
      const date = eventAxisValueToQueryDate(xAxisValue);
      const et = item?.event_type_id ?? item?.event_type?.id;
      const query: Record<string, string> = {
        timeType: 'Day',
        date,
        dashboardType: 'overview',
        fromPage: history.location.pathname,
        ...(eventBubbleDetailsNavCfg.query ?? {}),
      };
      if (et !== undefined && et !== null && String(et) !== '') {
        query.eventType = String(et);
      }
      history.push({ pathname: eventDetailsPagePath, query });
    },
    [eventBubbleDetailsNavCfg, eventDetailsPagePath],
  );

  const [showEvent, setShowEvent] = useState(defaultShowEvent);
  /**
   * KPI 总览：上一轮筛选条「显示事件」总开关（仅 kpiShared 存在时有意义）。
   * 用于在「关 → 开」边沿把所有本卡 Switch 置为勾选（需求 1），且不干扰总开关已开期间用户单独拨动本卡 Switch（需求 3）。
   */
  const prevFilterShowEventsMasterRef = useRef<boolean | null>(null);
  const [eventSettingVisible, setEventSettingVisible] = useState(false);
  const [chartInstance, setChartInstance] = useState<echarts.ECharts | null>(
    null,
  );
  const [eventResizeKey, setEventResizeKey] = useState(0);
  const chartRef = useRef<ChartRef>(null);
  /** KPI 横轴事件浮窗根节点 ref（透传给 EventDetailsPop / 新 hook） */
  const kpiEventPopRef = useRef<HTMLDivElement | null>(null);
  // 用于测量卡片头部高度，动态定位事件容器
  const headerRef = useRef<HTMLDivElement>(null);
  const [eventWrapperTop, setEventWrapperTop] = useState<number>(90);

  // Radio 状态管理
  const [selectedRadioValue, setSelectedRadioValue] = useState<string>(
    radioConfig?.defaultValue || radioConfig?.options?.[0]?.value || '',
  );

  // 外部受控：Radio
  const effectiveRadioValue =
    controlledRadioValue !== undefined
      ? controlledRadioValue
      : selectedRadioValue;

  // data[apiDataKey] 运行时除 data/errorMsg/loading 外还包含 req（ApiDataWrapper 类型未显式声明 req）
  type TApiSlot = ApiDataWrapper<ChartData> & {
    req?: {
      data_analysis?: unknown;
      data_key?: unknown;
      data_keys?: Record<string, string | undefined>;
      ai_analysis?: Record<string, string | undefined>;
      [key: string]: unknown;
    };
  };
  const apiSlot: TApiSlot = (data?.[apiDataKey] || {}) as TApiSlot;
  const {
    data: chartData,
    errorMsg,
    loading,
    req,
  } = apiSlot;
  const { data: propsEventData, loading: eventLoading } =
    data?.[eventKey] || {};
  const typedPropsEventData = useMemo(() => {
    if (!propsEventData || typeof propsEventData !== 'object') return undefined;
    return propsEventData as PropsEventData;
  }, [propsEventData]);
  const { xAxisData = [], seriesData = [] } = (chartData ||
    {}) as Partial<ChartData>;
  const title = getName(isEN, titleEn, titleCn);
  useEffect(() => {
    const chart = chartRef.current?.instance?.();
    if (chart) {
      setChartInstance(chart);
    }
  }, [loading, isEN]);
  useEffect(() => {
    const handleResize = debounce(() => {
      if (chartInstance) {
        chartInstance.resize(); // 重要：调整图表大小
      }
      setEventResizeKey((prev) => prev + 1); // 触发 EventWindows 重新渲染
    }, 200);

    window.addEventListener('resize', handleResize);

    return () => {
      handleResize.cancel();
      window.removeEventListener('resize', handleResize);
    };
  }, [chartInstance]);

  // ===== AI 副标题逻辑 =====
  const currentAiTag = aiTagMap?.[effectiveRadioValue] || aiTag || '';
  const alignmentAiDataKeyField = getAlignmentAiDataKeyField(
    effectiveRadioValue,
    alignmentDataKeyMap,
  );
  const alignmentAiDataKey = enableAlignmentAiDataKey && alignmentAiDataKeyField
    ? req?.data_keys?.[alignmentAiDataKeyField]
    : undefined;
  const currentAiDataKey = alignmentAiDataKey || req?.data_key;
  const aiSummary = alignmentAiDataKey
    ? req?.ai_analysis?.[alignmentAiDataKeyField]
    : req?.data_analysis;
  const { data: aiData, loading: aiLoading } = useAiSummary({
    game_code: data?.gameCode as string,
    data_key: currentAiDataKey as string,
    tag: currentAiTag,
    enabled: !loading && !!currentAiTag && !aiSummary && !!chartData,
  });

  // 处理图表数据 - 支持动态替换 metricKey 和 dateKey
  const { lineSeries, hasData } = useMemo(() => {
    // 根据 Radio 选择获取当前的配置
    const currentRadioOption = radioConfig?.options?.find(
      (opt: RadioOption) => opt.value === effectiveRadioValue,
    );

    // 根据配置动态生成折线图 series
    const lineSeries = seriesConfig?.map(
      (config: SeriesConfig, index: number) => {
        const {
          cnName,
          enName,
          color,
          formatRule,
          customSeriesConfig,
          abnormalSymbolColor,
          metricKey,
          dateKey,
          ...restConfig
        } = config;
        let finalMetricKey = metricKey;
        let finalDateKey = dateKey;
        let finalCnName = cnName;
        let finalEnName = enName;

        if (radioConfig && currentRadioOption) {
          // 新版：replacements 数组模式，支持任意数量的目标系列
          const { replacements } = currentRadioOption;
          if (Array.isArray(replacements)) {
            // radioConfig 形态由上游配置决定，targetSeriesIndexes 为目标系列索引数组
            const targetIndexes =
              (radioConfig as { targetSeriesIndexes?: number[]; }).targetSeriesIndexes || [];
            const replaceIdx = targetIndexes.indexOf(index);
            if (replaceIdx !== -1 && replacements[replaceIdx]) {
              const r = replacements[replaceIdx];
              finalMetricKey = r.metricKey || finalMetricKey;
              finalDateKey = r.dateKey || finalDateKey;
              finalCnName = r.cnName || finalCnName;
              finalEnName = r.enName || finalEnName;
            }
          }
          // 旧版兼容：targetSeriesIndex 单个目标（DAU 图等现有配置）
          else if (index === radioConfig.targetSeriesIndex) {
            finalMetricKey = currentRadioOption.metricKey;
            finalDateKey = currentRadioOption.dateKey;
            if (currentRadioOption.cnName)
              finalCnName = currentRadioOption.cnName;
            if (currentRadioOption.enName)
              finalEnName = currentRadioOption.enName;
          }
        }

        // 从 chartData 中根据 metricKey 获取数据
        const data =
          seriesData?.map((item: SeriesDataItem) => {
            const value = item[finalMetricKey] as string | number;

            // 如果配置了异常点颜色且当前点是异常点，返回对象格式
            if (abnormalSymbolColor && item.is_abnormal === true) {
              return {
                value,
                itemStyle: {
                  color: abnormalSymbolColor,
                  borderColor: abnormalSymbolColor,
                },
                symbol: 'circle',
                symbolSize: 8,
              };
            }

            return value;
          }) || [];

        const rest = restConfig as { showSymbol?: boolean; };
        let resolvedShowSymbol = true;
        if (chartConfig?.showSymbol === false) {
          resolvedShowSymbol = false;
        } else if (rest.showSymbol !== undefined) {
          resolvedShowSymbol = rest.showSymbol;
        }

        const colorResolved =
          typeof color === 'string' && color.trim() !== ''
            ? color.trim()
            : undefined;

        const baseSeries = {
          name: getName(isEN, finalEnName, finalCnName),
          type: 'line',
          data,
          symbol: 'circle',
          symbolSize: 6,
          labelLayout: {
            hideOverlap: true,
          },
          dateKey: finalDateKey,
          ...customSeriesConfig,
          ...restConfig,
          showSymbol: resolvedShowSymbol,
        };

        if (!colorResolved) {
          return baseSeries;
        }

        const bs = baseSeries as Record<string, unknown>;
        const prevItem = bs.itemStyle as Record<string, unknown> | undefined;
        const prevLine = bs.lineStyle as Record<string, unknown> | undefined;

        return {
          ...baseSeries,
          itemStyle: {
            ...(typeof prevItem === 'object' && prevItem !== null
              ? prevItem
              : {}),
            color: colorResolved,
          },
          lineStyle: {
            width: 2,
            ...(typeof prevLine === 'object' && prevLine !== null
              ? prevLine
              : {}),
            color: colorResolved,
          },
        };
      },
    );

    const hasData = forceNoDataProp
      ? false
      : lineSeries?.some((series) => series.data && series.data.length > 0);

    return {
      lineSeries,
      hasData,
    };
  }, [
    seriesData,
    isEN,
    seriesConfig,
    effectiveRadioValue,
    radioConfig,
    chartConfig?.showSymbol,
    forceNoDataProp,
  ]);

  // 根据 Radio 映射事件数据源；合并前各日条数供详情跳转阈值（与弹窗内「实验平铺」后的展示条数解耦）
  const { rawEventDataMap, preCollapseEventCountByAxis } = useMemo(() => {
    if (!typedPropsEventData) {
      return {
        rawEventDataMap: undefined as EventDataMap | undefined,
        preCollapseEventCountByAxis: {} satisfies Record<string, number>,
      };
    }

    let base: EventDataMap | undefined;

    if (!radioConfig?.options) {
      base = typedPropsEventData?.eventData;
    } else {
      const currentRadioOption = radioConfig.options.find(
        (opt: RadioOption) => opt.value === effectiveRadioValue,
      );

      const targetEventDataKey =
        currentRadioOption?.eventDataKey || 'eventData';

      const targetData = typedPropsEventData[targetEventDataKey];
      if (
        targetData &&
        typeof targetData === 'object' &&
        !Array.isArray(targetData)
      ) {
        base = targetData as EventDataMap;
      } else {
        base = typedPropsEventData.eventData;
      }
    }

    const baseForDisplay = filterEventDataMapDropStartBeforeFirstAxisTick(
      base,
      xAxisData,
      eventStartTimeAxisClipResolved,
    );

    const preCollapseEventCountByAxis: Record<string, number> = {};
    if (baseForDisplay) {
      Object.keys(baseForDisplay).forEach(k => {
        const arr = baseForDisplay![k];
        preCollapseEventCountByAxis[k] = Array.isArray(arr) ? arr.length : 0;
      });
    }

    return {
      rawEventDataMap: baseForDisplay,
      preCollapseEventCountByAxis,
    };
  }, [typedPropsEventData, radioConfig, effectiveRadioValue, xAxisData, eventStartTimeAxisClipResolved]);

  const {
    chartEventSetting,
    changeEventSetting,
    timeFormat,
    isAxisBubbleControlledByFilter,
  } = useEventMetricTrendKpiEventUi({
    enabled: kpiUi.enabled,
    processedEventData: rawEventDataMap,
    xAxisData,
    kpiEventFromFilter: kpiShared,
  });

  /**
   * KPI 事件卡：在存在实验类合并配置时（默认与 `event_type` 键「实验」对齐，或由 `chartEventExperimentTooltip` 扩展），
   * 合并后主文案统一为「A/B 实验(n)」，无需每卡再配 useExperimentTypeFixedDisplayName。
   * 非 KPI 仍仅在该开关为 true 时启用固定文案。
   */
  const experimentFixedForEventPipeline: ExperimentTypeFixedLabelParams | undefined =
    effectiveEventTypeCollapseConfig &&
    (kpiFilterBubblePipeline || chartConfig?.useExperimentTypeFixedDisplayName)
      ? {
        enabled: true,
        labelCn: chartConfig?.experimentTypeFixedLabelCn ?? 'A/B 实验',
        labelEn: chartConfig?.experimentTypeFixedLabelEn ?? 'A/B Test',
      }
      : undefined;

  const eventData = useMemo(() => {
    if (!rawEventDataMap) {
      return undefined;
    }
    const experimentFlatEnabled =
      chartEventSetting.showExperimentEventDetailFlat === true;
    const afterCollapse = experimentFlatEnabled
      ? rawEventDataMap
      : applyEventTypeCollapseToEventDataMap(
        rawEventDataMap,
        effectiveEventTypeCollapseConfig,
        isEN,
        experimentFixedForEventPipeline,
      );
    const afterOrder = applyEventTypeDisplayOrderToEventDataMap(
      afterCollapse,
      chartConfig?.eventTypeDisplayOrder,
    );
    return applyEventTypeBarColorMapToEventDataMap(
      afterOrder,
      chartConfig?.eventTypeBarColorMap,
    );
  }, [
    rawEventDataMap,
    chartEventSetting.showExperimentEventDetailFlat,
    effectiveEventTypeCollapseConfig,
    chartConfig?.eventTypeDisplayOrder,
    chartConfig?.eventTypeBarColorMap,
    kpiFilterBubblePipeline,
    chartConfig?.useExperimentTypeFixedDisplayName,
    chartConfig?.experimentTypeFixedLabelCn,
    chartConfig?.experimentTypeFixedLabelEn,
    isEN,
  ]);

  /**
   * 筛选条「显示事件」总开关（只读）；与 KpiEventFilter 勾选一致。
   * 注意：走 hook 返回的 chartEventSetting（已对来自筛选条的 axis/bubbles 做防抖），
   * 而非直接读 kpiShared.chartEventSetting；保证下游 useEffect / useMemo 收到的
   * 总开关与各 echarts setOption 触发节奏一致。
   */
  const filterShowEventsMaster =
    !kpiShared ||
    chartEventSetting.showEventAxis ||
    chartEventSetting.showEventBubbles;

  /** 有 KPI 注入且筛选主 Checkbox 关（轴/气泡均为关）；本卡 Switch 开时仍在本卡展示事件，不反写筛选 */
  const filterMasterOff = Boolean(
    kpiShared &&
      !chartEventSetting.showEventAxis &&
      !chartEventSetting.showEventBubbles,
  );

  /**
   * KPI 总览「显示事件」与本卡 Switch / 事件层（无 kpiShared 时仅用 showEvent）：
   * 1）筛选器勾选「显示事件」：总开关由关→开时，把所有本卡 showEvent 置 true，各卡 Switch 为开。
   * 2）筛选器取消「显示事件」：总开关为关时，把所有本卡 showEvent 置 false，各卡 Switch 为关；之后本卡 Switch 可再打开，本卡事件层/气泡仍展示（筛选轴/气泡仍为关，不回写）。
   * 3）总开关为开：Switch 只 setShowEvent；轴/气泡细分仍跟筛选条 chartEventSetting（及弹窗在允许时）只读同步。
   */
  const eventSwitchValue =
    controlledShowEvent !== undefined ? controlledShowEvent : showEvent;

  // 动态测量头部高度，精确定位事件容器（AI 文案行数不同高度不同）
  useEffect(() => {
    if (headerRef.current) {
      const headerHeight = headerRef.current.getBoundingClientRect().height;
      setEventWrapperTop(headerHeight + 38);
    }
  }, [
    loading,
    aiLoading,
    aiData,
    aiSummary,
    eventSwitchValue,
    filterMasterOff,
    chartEventSetting.showEventAxis,
    chartEventSetting.showEventBubbles,
    effectiveShowTableView,
    viewChartType,
  ]);

  const handleChartEventSwitchChange = useCallback(
    (value: boolean) => {
      if (controlledShowEvent !== undefined) {
        return;
      }
      setShowEvent(value);
    },
    [controlledShowEvent],
  );

  useEffect(() => {
    if (controlledShowEvent !== undefined || !kpiShared) {
      return;
    }
    const master = filterShowEventsMaster;
    const prev = prevFilterShowEventsMasterRef.current;

    /**
     * 边沿触发：只在 master 真正翻转时同步本卡 Switch。
     * 仅靠 `if (!master) setShowEvent(false)` 会变成「低电平触发」——
     * filter 弹窗确认 bubbleEvent 时 kpiSharedEventSetting 是新引用、kpiShared deps 变化导致
     * effect 重跑，会把用户手动打开的本卡 Switch 再次强制关掉（master 还是 false 时也触发）。
     *
     * 首次挂载（prev === null）保留兼容兜底：若 master 已为关，则把本卡 Switch 对齐为关，
     * 避免改变历史「进入页面时跟随 master 默认状态」的体验。
     */
    if (prev === null) {
      if (!master) {
        setShowEvent(false);
      }
    } else if (prev === true && !master) {
      setShowEvent(false);
    } else if (prev === false && master) {
      setShowEvent(true);
    }

    prevFilterShowEventsMasterRef.current = master;
  }, [controlledShowEvent, kpiShared, filterShowEventsMaster]);

  /**
   * 图表区 EventWindows：是否对「连续自然日重复事件」做合并。
   * 前置到 `eventDataForKpiLayer` 之前定义，便于在 collapse 之前先 merge。
   * `chartConfig.mergeConsecutiveTrendDaysForChartEventWindows === false` 时关闭。
   */
  const kpiMergeForChartEventWindows =
    kpiFilterBubblePipeline &&
    chartConfig?.mergeConsecutiveTrendDaysForChartEventWindows !== false;

  const eventDataForKpiLayer = useMemo(() => {
    if (!kpiFilterBubblePipeline) {
      return eventData;
    }
    const useBubblePipeline =
      chartEventSetting.showEventBubbles ||
      (filterMasterOff && eventSwitchValue);
    if (!useBubblePipeline) {
      return eventData;
    }
    const base = chartEventSetting.bubbleEvent;

    return applyChartEventWindowsPipelineToEventDataMap(base, {
      mergeConsecutiveTrendDays: kpiMergeForChartEventWindows,
      eventTypeCollapseConfig: effectiveEventTypeCollapseConfig,
      showExperimentEventDetailFlat:
        chartEventSetting.showExperimentEventDetailFlat,
      isEn: isEN,
      experimentFixedLabel: experimentFixedForEventPipeline,
      eventTypeDisplayOrder:
        chartConfig?.eventTypeDisplayOrder?.length &&
        chartConfig.eventTypeDisplayOrder.length > 0
          ? chartConfig.eventTypeDisplayOrder
          : undefined,
      eventTypeBarColorMap: chartConfig?.eventTypeBarColorMap,
    });
  }, [
    kpiFilterBubblePipeline,
    kpiMergeForChartEventWindows,
    chartEventSetting.showEventBubbles,
    chartEventSetting.showExperimentEventDetailFlat,
    filterMasterOff,
    eventSwitchValue,
    chartEventSetting.bubbleEvent,
    eventData,
    effectiveEventTypeCollapseConfig,
    chartConfig?.eventTypeDisplayOrder,
    chartConfig?.eventTypeBarColorMap,
    chartConfig?.useExperimentTypeFixedDisplayName,
    chartConfig?.experimentTypeFixedLabelCn,
    chartConfig?.experimentTypeFixedLabelEn,
    isEN,
  ]);

  /**
   * X 轴事件数字气泡 + 拐点 hover 浮窗（kpiEventDetailsPopDays / kpiEventCurrent）使用未做实验整合的「原始」事件数据。
   * 与 eventDataForKpiLayer 的区别：
   *   1) 判断条件与 showKpiEventAxisRow 对齐——X 轴气泡显隐由 showEventAxis（或 master 关）决定，
   *      而非 showEventBubbles（那是上方 EventWindows 的开关）；用错条件会让本数据流退回 collapsed 的 eventData。
   *   2) 跳过 applyEventTypeCollapseToEventDataMap（A/B 实验合并），但保留排序 + 颜色映射；
   *      这样上方 EventWindows 可显示「A/B Test(n)」聚合标签的同时，
   *      下方横轴气泡的数字与 hover 列表仍按原始每日条数展示。
   */
  const eventDataForKpiXAxis = useMemo(() => {
    if (!kpiFilterBubblePipeline) {
      return eventData;
    }
    const useAxisPipeline =
      chartEventSetting.showEventAxis ||
      (filterMasterOff && eventSwitchValue);
    if (!useAxisPipeline) {
      return eventData;
    }
    const orderList =
      chartConfig?.eventTypeDisplayOrder?.length &&
      chartConfig.eventTypeDisplayOrder.length > 0
        ? chartConfig.eventTypeDisplayOrder
        : [...KPI_DEFAULT_EVENT_TYPE_PRIORITY];
    const afterOrder = applyEventTypeDisplayOrderToEventDataMap(
      chartEventSetting.bubbleEvent,
      orderList,
    );
    return applyEventTypeBarColorMapToEventDataMap(
      afterOrder,
      chartConfig?.eventTypeBarColorMap,
    );
  }, [
    kpiFilterBubblePipeline,
    chartEventSetting.showEventAxis,
    filterMasterOff,
    eventSwitchValue,
    chartEventSetting.bubbleEvent,
    eventData,
    chartConfig?.eventTypeDisplayOrder,
    chartConfig?.eventTypeBarColorMap,
  ]);

  const chartRenderEventListForBubbleCounts = useMemo(
    () => buildChartRenderEventList(eventDataForKpiXAxis),
    [eventDataForKpiXAxis],
  );

  /**
   * EventWindows 用的最终事件 map：`eventDataForKpiLayer` 内部已先做连续日合并再 collapse，
   * 这里直接复用，无需再合并一次（避免 collapse 后 name/(n) 被二次 merge 改写）。
   */
  const eventListForKpiEventWindows: EventDataMap | undefined =
    eventDataForKpiLayer;
  /** KPI：X 轴旁事件条数数字气泡（中性）；与各类型浮窗 Tag 色分离 */
  const kpiXAxisBubbleColors = useMemo(
    () =>
      extractKpiXAxisBubbleLabelColors(
        chartConfig?.eventTypeBarColorMap,
        chartConfig?.kpiXAxisEventCountBubble,
      ),
    [
      chartConfig?.eventTypeBarColorMap,
      chartConfig?.kpiXAxisEventCountBubble,
    ],
  );
  /** KPI：浮窗内 EventTypeTag 与舆情条色块对齐 */
  const kpiEventTagStyleMap = useMemo(
    () =>
      buildEventTypeTagStyleMapFromBarColorMap(
        chartConfig?.eventTypeBarColorMap,
        chartConfig?.eventTypeTagStyleAliases,
      ),
    [
      chartConfig?.eventTypeBarColorMap,
      chartConfig?.eventTypeTagStyleAliases,
    ],
  );

  /**
   * 拐点气泡数字 + 底部 EventDetailsPop：**未合并**逐日数据，条数与列表一致。
   * 图表区浮动标签见 `eventListForKpiEventWindows`（合并后）。
   */
  const kpiEventDetailsPopDays = useMemo(
    () =>
      buildEventDetailsPopAlignedDays(
        xAxisData as string[],
        chartRenderEventListForBubbleCounts,
      ),
    [xAxisData, chartRenderEventListForBubbleCounts],
  );

  const {
    currentEvents: kpiEventCurrent,
    attachListenersFunc: kpiEventAttachListeners,
    appendEventDetailsSeries: kpiEventAppendSeries,
    kpiPopSurfaceHoverRef,
  } = useEventMetricTrendKpiEventDetailsPop(
    kpiEventDetailsPopDays,
    kpiEventPopRef,
    kpiUi.enabled
      ? { xAxisBubbleLabelColors: kpiXAxisBubbleColors ?? undefined }
      : undefined,
  );

  /**
   * 「KPI 横轴事件 hover 浮窗」监听只能挂一次（echarts.on 不去重），包一层守卫：
   * 第一次挂上后置位，后续重复调用直接 no-op。
   * 这样可去掉 LineChart 的 key remount，改由 useEffect 主动补挂——
   * 避免「勾选事件 → key 变 → echarts 实例销毁重建 → 整图重画」。
   */
  const kpiListenersAttachedRef = useRef(false);
  const wrappedKpiEventAttachListeners = useCallback(
    (opt: EChartsOption, chart: unknown): EChartsOption => {
      if (kpiListenersAttachedRef.current) {
        return opt;
      }
      kpiListenersAttachedRef.current = true;
      return kpiEventAttachListeners(opt, chart);
    },
    [kpiEventAttachListeners],
  );

  const eventModalDataKey = buildRawEventListForModalDataKey(
    typedPropsEventData?.rawEventList as
      | Array<Record<string, unknown>>
      | undefined,
  );
  const eventSettingModalData = useMemo(
    () =>
      groupRawEventListByTypeForModal(
        typedPropsEventData?.rawEventList as
          | Array<Record<string, unknown>>
          | undefined,
      ),
    [eventModalDataKey],
  );

  const eventSettingModalNode = kpiUi.settings ? (
    <EventMetricTrendEventSettingModal
      visible={eventSettingVisible}
      close={() => setEventSettingVisible(false)}
      confirm={(payload) => {
        setEventSettingVisible(false);
        changeEventSetting({
          bubbleEvent: payload.bubbleEvent as EventDataMap,
          showEventAxis: isAxisBubbleControlledByFilter
            ? chartEventSetting.showEventAxis
            : payload.showEventAxis,
          showEventBubbles: isAxisBubbleControlledByFilter
            ? chartEventSetting.showEventBubbles
            : payload.showEventBubbles,
          showExperimentEventDetailFlat: payload.showExperimentEventDetailFlat,
        });
      }}
      data={eventSettingModalData}
      timeFormat={timeFormat}
      showEventAxisValue={chartEventSetting.showEventAxis}
      showEventBubblesValue={chartEventSetting.showEventBubbles}
      showExperimentEventDetailFlatValue={
        chartEventSetting.showExperimentEventDetailFlat ?? false
      }
      dateType="daily"
      xAxisData={xAxisData}
      hideAxisBubbleToggles={isAxisBubbleControlledByFilter}
    />
  ) : null;

  const eventDetailsNavForWindows = useMemo(() => {
    if (!eventBubbleDetailsNavCfg) {
      return undefined;
    }
    const minCountExclusive = eventBubbleDetailsNavCfg.minCountExclusive ?? 3;
    return {
      minCountExclusive,
      onNavigate: handleEventBubbleDetailsNavigate,
      // 经分侧按合并前当日条数判断，舆情层只消费布尔结果
      shouldAllowDetailsNav: (xAxisValue: string): boolean =>
        (preCollapseEventCountByAxis[xAxisValue] ?? 0) > minCountExclusive,
    };
  }, [
    eventBubbleDetailsNavCfg,
    handleEventBubbleDetailsNavigate,
    preCollapseEventCountByAxis,
  ]);

  /** 仅有日期 key、每日 [] 时不应占事件层（与 KPI 等数据源对齐） */
  const hasRenderableEvents = useMemo(
    () =>
      Object.values(eventData ?? {}).some(
        (arr) => Array.isArray(arr) && arr.length > 0,
      ),
    [eventData],
  );

  const showEventLayer =
    eventSwitchValue &&
    hasRenderableEvents &&
    (!kpiUi.enabled ||
      filterMasterOff ||
      chartEventSetting.showEventBubbles);

  const showKpiEventAxisRow =
    kpiUi.enabled &&
    kpiUi.xAxisRow &&
    (filterMasterOff || chartEventSetting.showEventAxis) &&
    eventSwitchValue &&
    hasRenderableEvents;

  /** EventWindows 轴上数字：总开关关且本卡 Switch 开时仍展示 */
  const eventWindowsShowEventAxis = kpiUi.enabled
    ? (filterMasterOff && eventSwitchValue) || chartEventSetting.showEventAxis
    : true;

  /**
   * 在 KPI 详情浮层根节点上同步「指针是否在浮层上」，
   * 供 useEventMetricTrendKpiEventDetailsPop 在 globalout 时延迟隐藏，避免叠在舆情气泡上闪烁。
   */
  useLayoutEffect(() => {
    if (!showKpiEventAxisRow) {
      kpiPopSurfaceHoverRef.current = false;
      return;
    }
    const el = kpiEventPopRef.current;
    if (!el) {
      return;
    }
    const onEnter = (): void => {
      kpiPopSurfaceHoverRef.current = true;
    };
    const onLeave = (): void => {
      kpiPopSurfaceHoverRef.current = false;
    };
    el.addEventListener('pointerenter', onEnter);
    el.addEventListener('pointerleave', onLeave);
    return (): void => {
      el.removeEventListener('pointerenter', onEnter);
      el.removeEventListener('pointerleave', onLeave);
    };
  }, [showKpiEventAxisRow, kpiPopSurfaceHoverRef]);

  /**
   * showKpiEventAxisRow 切换时，同步父状态用的 chartInstance，并对已存在的 echarts 实例补挂 KPI 事件监听。
   * 不再 remount LineChart（key 已移除），所以同一实例上：
   *   - 首屏 showKpiEventAxisRow=true 时：Chart init 内的 chartEventConfig 已经挂过监听 → 守卫挡掉补挂。
   *   - 首屏为 false、后来变 true：Chart init 时 chartEventConfig 是 undefined 没挂过 → 此处补挂一次。
   */
  useEffect(() => {
    const chart = chartRef.current?.instance?.();
    if (!chart) {
      return;
    }
    setChartInstance(chart);
    if (showKpiEventAxisRow) {
      wrappedKpiEventAttachListeners(
        chart.getOption() as EChartsOption,
        chart,
      );
    }
  }, [showKpiEventAxisRow, wrappedKpiEventAttachListeners]);

  /**
   * 事件层显隐切换后，让 EventWindows 通过 key 变化重新 mount，使 EventItem 重新 convertToPixel 拿坐标。
   *
   * 背景：去掉 LineChart 的 key remount 之后，echarts 实例保持不变（chartInstance 引用不变），
   * 而 EventItem 的「重算位置」useEffect 依赖 [myChart]——引用不变就不会重跑，沿用上一次的坐标
   * （`showEventLayer=false` 时 TopTipsEvent series 不存在，convertToPixel 抛错被 catch 返回 0），
   * 表现为所有事件浮窗堆在左上角。
   *
   * 实现：监听 showEventLayer / showKpiEventAxisRow，跳过首屏；变更后用一次性 'finished' 事件
   * （+ 双 RAF 兜底，避免某些场景 echarts 不触发 finished）等 setOption 落地，再 setEventResizeKey++。
   */
  const prevEventVisibilityRef = useRef<string>('init');
  useEffect(() => {
    if (!chartInstance) {
      return;
    }
    const key = `${showEventLayer ? '1' : '0'}-${
      showKpiEventAxisRow ? '1' : '0'
    }`;
    if (prevEventVisibilityRef.current === key) {
      return;
    }
    const isFirstRun = prevEventVisibilityRef.current === 'init';
    prevEventVisibilityRef.current = key;
    if (isFirstRun) {
      return;
    }

    let raf1Id = 0;
    let raf2Id = 0;
    let done = false;
    const bumpResizeKey = (): void => {
      if (done) return;
      done = true;
      setEventResizeKey((prev) => prev + 1);
    };
    const onFinished = (): void => {
      chartInstance.off('finished', onFinished);
      bumpResizeKey();
    };
    chartInstance.on('finished', onFinished);
    raf1Id = requestAnimationFrame(() => {
      raf2Id = requestAnimationFrame(() => {
        bumpResizeKey();
      });
    });

    return (): void => {
      chartInstance.off('finished', onFinished);
      cancelAnimationFrame(raf1Id);
      cancelAnimationFrame(raf2Id);
    };
  }, [chartInstance, showEventLayer, showKpiEventAxisRow]);

  const rawEventList = useMemo(
    () =>
      filterRawEventListDropStartBeforeFirstAxisTick(
        typedPropsEventData?.rawEventList,
        xAxisData,
        eventStartTimeAxisClipResolved,
      ),
    [
      typedPropsEventData?.rawEventList,
      xAxisData,
      eventStartTimeAxisClipResolved,
    ],
  );

  /**
   * 列来源：pageConfig.downloadConfig.columns，或 KpiEventDimensionTrendService 写入首行的 _dlCols。
   * 后者与 downloadConfig: {} 并存时，原先用 truthy 的 downloadConfig 会走 formatEventDataSource 但 columns 为空，导致事件列无数据。
   */
  const effectiveDownloadColumns = useMemo(() => {
    const fromConfig = downloadConfig?.columns;
    if (Array.isArray(fromConfig) && fromConfig.length > 0) {
      return fromConfig;
    }
    const first = seriesData?.[0] as Record<string, unknown> | undefined;
    const embedded = first?._dlCols;
    return Array.isArray(embedded) ? embedded : undefined;
  }, [downloadConfig?.columns, seriesData]);

  const getDataSource = (): SeriesDataItem[] => {
    const cols = effectiveDownloadColumns;
    const hasEventCol = cols?.some(
      (col: { type?: string; }) => col?.type === 'event',
    );
    if (
      Array.isArray(rawEventList) &&
      rawEventList.length > 0 &&
      hasEventCol &&
      Array.isArray(cols) &&
      cols.length > 0
    ) {
      return formatEventDataSource(
        seriesData as { date: string; }[],
        rawEventList,
        {
          columns: cols as unknown as {
            dependKey: string;
            code: string;
            dateKey: string;
          }[],
        },
        isEN,
      ) as SeriesDataItem[];
    }
    return seriesData || [];
  };

  /** 与图表同一 isEN；下载前解析 nameEn/nameCn（解析实现见 KPI service _utils） */
  const downloadColumnsForExcel = useMemo(() => {
    const cols = effectiveDownloadColumns;
    let picked: Array<Record<string, unknown>> | undefined;
    if (!cols?.length) {
      picked = downloadConfig?.columns as unknown as
        | Array<Record<string, unknown>>
        | undefined;
    } else if (!radioConfig) {
      picked = cols as unknown as Array<Record<string, unknown>>;
    } else {
      picked = (cols as DownloadColumn[]).filter(
        (column: DownloadColumn) =>
          !column.radioValue || column.radioValue === effectiveRadioValue,
      ) as unknown as Array<Record<string, unknown>>;
    }
    return resolveKpiMetricTrendDownloadColumns(picked, isEN) as
      | DownloadColumn[]
      | undefined;
  }, [
    effectiveDownloadColumns,
    downloadConfig?.columns,
    radioConfig,
    effectiveRadioValue,
    isEN,
    locale,
  ]);

  const trendTableModel = useEventMetricTrendTable({
    effectiveShowTableView,
    seriesConfig,
    seriesData,
    radioConfig,
    effectiveRadioValue,
    isEN,
    formatMessage,
    rawEventList,
    downloadColumnsResolved:
      downloadColumnsForExcel as Array<Record<string, unknown>> | undefined,
  });

  /** key/header 列结构 → ali-react-table（CommonBaseTable） */
  const eventTrendBaseTableModel = useMemo(() => {
    if (!trendTableModel) return null;
    const columns: ArtColumn[] = trendTableModel.tableOption.map(col => ({
      code: String(col.key),
      name: String(col.header ?? col.key),
      align: col.align === 'right' ? 'right' : 'left',
      width: col.width,
      minWidth: col.minWidth,
    }));
    const dataSource = trendTableModel.tableList.map((row, index) => ({
      ...row,
      __rowId: `trend-row-${index}`,
    }));
    return { columns, dataSource };
  }, [trendTableModel]);

  const trendSheetTableContent = useMemo(() => {
    if (loading) {
      return (
        <div
          className="dbd-event-metric-trend-table-loading"
          style={{ height: tableViewHeight }}
        >
          <Loading />
        </div>
      );
    }
    if (!eventTrendBaseTableModel) return null;
    return (
      <CommonBaseTable
        className="dbd-event-metric-trend-base-table"
        columns={eventTrendBaseTableModel.columns}
        dataSource={eventTrendBaseTableModel.dataSource}
        tableConfig={{
          tableHeight: tableViewHeight,
          isShowPagination: false,
          primaryKey: '__rowId',
          wrapperClassName: 'dbd-event-metric-trend-base-table-wrap',
          /** 使用 CommonBaseTable 已有配置：跳过 UseMonitorVisible 懒挂载，避免 Tab 内首帧高度为 0 */
          skipViewportVisibilityLazy: true,
          wrapperStyle: {
            minHeight: tableViewHeight,
            height: tableViewHeight,
          },
        }}
        featConfig={{ autoColumnWidth: { maxWidth: 200, minWidth: 80 } } }
      />
    );
  }, [loading, tableViewHeight, eventTrendBaseTableModel]);

  const formatTooltipAxisCore = useCallback(
    (params: TooltipParams[]) => {
      const currentRadioOption = radioConfig?.options?.find(
        (opt: RadioOption) => opt.value === effectiveRadioValue,
      );

      const resolveTooltipSeriesName = (
        seriesIndex: number,
        fallback: string,
      ): string => {
        const cfg = seriesConfig?.[seriesIndex];
        if (!cfg) {
          return fallback;
        }
        let tipEn = cfg.tooltipEnName as string | undefined;
        let tipCn = cfg.tooltipCnName as string | undefined;
        if (radioConfig && currentRadioOption) {
          const { replacements } = currentRadioOption;
          if (Array.isArray(replacements)) {
            const targetIndexes =
              (radioConfig as { targetSeriesIndexes?: number[]; })
                .targetSeriesIndexes || [];
            const rIdx = targetIndexes.indexOf(seriesIndex);
            const r = replacements[rIdx] as
              | { tooltipEnName?: string; tooltipCnName?: string; }
              | undefined;
            if (r?.tooltipEnName) {
              tipEn = r.tooltipEnName;
            }
            if (r?.tooltipCnName) {
              tipCn = r.tooltipCnName;
            }
          }
        }
        if (isEN && tipEn) {
          return tipEn;
        }
        if (!isEN && tipCn) {
          return tipCn;
        }
        return fallback;
      };

      const tooltipData: TooltipDataItem[] = params
        .filter(
          (item: TooltipParams) =>
            !seriesConfig[item.seriesIndex]?.hideInTooltip &&
            item.seriesName !== 'TopTipsEvent',
        )
        .map((item: TooltipParams) => {
          // ECharts tooltip 的 value/data 字段可能是原始类型或包装对象，这里需运行时判断
          let rawValue: unknown = item?.data?.value ?? item?.value ?? item?.data;
          if (typeof rawValue === 'object' && rawValue !== null) {
            rawValue = (rawValue as { value?: unknown; })?.value ?? '';
          }

          let dateKey = seriesConfig[item.seriesIndex]?.dateKey || 'date';

          if (radioConfig && currentRadioOption) {
            const { replacements } = currentRadioOption;
            if (Array.isArray(replacements)) {
              const targetIndexes =
                (radioConfig as { targetSeriesIndexes?: number[]; }).targetSeriesIndexes || [];
              const rIdx = targetIndexes.indexOf(item.seriesIndex);
              if (rIdx !== -1 && replacements[rIdx]?.dateKey) {
                dateKey = replacements[rIdx].dateKey;
              }
            } else if (item.seriesIndex === radioConfig.targetSeriesIndex) {
              dateKey = currentRadioOption.dateKey;
            }
          }

          const date = seriesData?.[item.dataIndex]?.[dateKey] as
            | string
            | undefined;

          const currentSeriesConfig = seriesConfig[item.seriesIndex] || {};
          const formattedValue =
            dataFormat({
              value: rawValue as number,
              // FormatRule.type 可选，IFormatRule.type 必填；运行时字段由配置侧保证，此处结构断言
              formatRule: currentSeriesConfig?.formatRule as unknown as IFormatRule,
            }) || '';

          let tooltipSeriesIconColor: string | undefined;
          if (
            kpiUi.enabled &&
            typeof chartConfig?.kpiEventUi === 'object' &&
            chartConfig?.kpiEventUi !== null &&
            !Array.isArray(chartConfig.kpiEventUi)
          ) {
            const c =
              chartConfig.kpiEventUi.tooltipSeriesIconColors?.[item.seriesIndex];
            if (typeof c === 'string' && c.trim() !== '') {
              tooltipSeriesIconColor = c.trim();
            }
          }

          return {
            name: resolveTooltipSeriesName(
              item.seriesIndex,
              item.seriesName || '',
            ),
            value: String(formattedValue) || '-',
            color: tooltipSeriesIconColor ?? item.color,
            date: typeof date === 'string' ? date : '',
            _hasData:
              rawValue !== undefined && rawValue !== null && rawValue !== '',
          };
        })
        .filter((item: TooltipDataItem & { _hasData?: boolean; }) => item._hasData);

      if (tooltipData.length === 0) {
        return '';
      }

      const dateGroups = new Map<string, TooltipDataItem[]>();
      tooltipData.forEach((item: TooltipDataItem) => {
        // item 上可能挂有 date 字段（由 series 数据注入），类型断言读取
        const itemDate = (item as { date?: string; }).date || '_nodate_';
        if (!dateGroups.has(itemDate)) {
          dateGroups.set(itemDate, []);
        }
        dateGroups.get(itemDate)!.push(item);
      });

      const tableArray: ITableOption[] = [];
      dateGroups.forEach((items, groupDate) => {
        if (groupDate !== '_nodate_') {
          tableArray.push({
            hideHeader: true,
            data: [{ name: dateTimeFormat(groupDate, 'YYYY-MM-DD') || '' }],
            columns: [
              { key: 'name', header: '', align: 'left' as const },
              { key: 'value', align: 'right' as const },
            ],
          });
        }
        items.forEach((item: TooltipDataItem) => {
          tableArray.push({
            hideHeader: true,
            className: 'series-row',
            data: [item],
            columns: [
              { key: 'name', align: 'left' as const, showIcon: true },
              { key: 'value', align: 'right' as const },
            ],
          });
        });
      });

      const tooltipHtml = getEchartTooltipString({
        table: tableArray,
      });
      const { xAxisFsScale: tooltipScale } = resolveFullscreenFontScales({
        isFullScreen: viewportFullscreen?.isFullScreen,
        scale: viewportFullscreen?.scale,
        xAxisScale: viewportFullscreen?.xAxisScale,
        chartFullscreenXAxisScale: chartConfig?.fullscreenXAxisScale,
      });
      if (tooltipScale === 1) {
        return tooltipHtml;
      }
      return `<div class="dbd-event-metric-trend-tooltip-scale" style="--dbd-tooltip-scale:${tooltipScale}">${tooltipHtml}</div>`;
    },
    [
      seriesConfig,
      seriesData,
      radioConfig,
      effectiveRadioValue,
      isEN,
      kpiUi.enabled,
      chartConfig?.kpiEventUi,
      chartConfig?.fullscreenXAxisScale,
      viewportFullscreen?.isFullScreen,
      viewportFullscreen?.scale,
      viewportFullscreen?.xAxisScale,
    ],
  );

  const tooltipFormatterFunc = useMemo(() => {
    const ms = chartConfig?.tooltipThrottleMs;
    if (typeof ms === 'number' && ms > 0) {
      return throttle(formatTooltipAxisCore, ms, {
        leading: true,
        trailing: true,
      });
    }
    return formatTooltipAxisCore;
  }, [chartConfig?.tooltipThrottleMs, formatTooltipAxisCore]);

  useEffect(() => {
    const fn = tooltipFormatterFunc as { cancel?: () => void; };
    return () => {
      if (fn && typeof fn.cancel === 'function') {
        fn.cancel();
      }
    };
  }, [tooltipFormatterFunc]);

  // 渲染副标题：AI 模式或静态模式
  const renderSubTitle = (): React.ReactNode => {
    // AI 模式：有 aiTag 配置时，显示 AI 动态副标题
    if (currentAiTag) {
      // 数据加载中 / AI 接口加载中，显示 loading
      if (loading || aiLoading) {
        return (
          <div className="dbd-event-metric-trend-subtitle">
            <Loading />
          </div>
        );
      }
      // 优先使用接口返回的缓存摘要，否则使用 AI 接口返回的数据
      const aiContent = aiSummary || aiData;
      if (aiContent) {
        return (
          <FormatHtmlTextToReact
            text={String(aiContent)}
            className="dbd-event-metric-trend-subtitle dbd-event-metric-trend-subtitle--ai"
          />
        );
      }
      return null;
    }
    // 静态模式：使用配置的 subTitle
    if (subTitle) {
      return (
        <div className="dbd-event-metric-trend-subtitle">
          {getName(isEN, subTitle, subTitleCn)}
        </div>
      );
    }
    return null;
  };

  const trendChartHeight = useMemo(() => {
    if (trendChartHeightProp !== undefined) {
      return trendChartHeightProp;
    }
    if (
      viewportFullscreen?.enabled &&
      viewportFullscreen?.isFullScreen
    ) {
      return Math.max(
        280,
        viewportFullscreen.viewportHeight -
          FULLSCREEN_EVENT_METRIC_TREND_CHROME_RESERVE,
      );
    }
    return effectiveShowTableView ? tableViewHeight : 375;
  }, [
    trendChartHeightProp,
    viewportFullscreen?.enabled,
    viewportFullscreen?.isFullScreen,
    viewportFullscreen?.viewportHeight,
    effectiveShowTableView,
    tableViewHeight,
  ]);

  const trendAbnormalOuterHeight = useMemo(
    () =>
      abnormalHeightProp ??
      (effectiveShowTableView ? tableViewHeight + 10 : trendChartHeight + 10),
    [abnormalHeightProp, effectiveShowTableView, tableViewHeight, trendChartHeight],
  );

  const trendBodyChart = (
    <Abnormal
      isLoading={!!loading}
      isError={!!errorMsg}
      style={{ height: trendAbnormalOuterHeight }}
      isNoData={!hasData}
      classNames="dbd-event-metric-trend-abnormal"
      component={
        <div style={{ position: 'relative', width: '100%' }}>
          <LineChart
            // 不再以 showKpiEventAxisRow 作为 key——避免开关事件时 echarts 实例销毁重建导致整图重画。
            // KPI hover 监听由 wrappedKpiEventAttachListeners + useEffect 兜底补挂，且自带守卫不会重复挂。
            ref={chartRef}
            xAxisData={xAxisData}
            series={lineSeries}
            height={trendChartHeight}
            chartEventConfig={
              showKpiEventAxisRow ? wrappedKpiEventAttachListeners : undefined
            }
            //   isShowLegend={true}
            formatterFunc={tooltipFormatterFunc}
            setOtherConfigFunc={(option: EChartsOption) => {
              // option.tooltip 在 EChartsOption 内是复杂联合类型，此处仅按对象结构读取 axisPointer
              const prevTip = (option as { tooltip?: Record<string, unknown>; }).tooltip || {};
              const prevAp =
                typeof prevTip.axisPointer === 'object' &&
                prevTip.axisPointer !== null
                  ? prevTip.axisPointer
                  : {};
              option.tooltip = {
                ...prevTip,
                backgroundColor: 'transparent',
                borderWidth: 0,
                padding: 0,
                transitionDuration: 0,
                axisPointer: {
                  ...prevAp,
                  animation: false,
                },
              };
              option.yAxis = [
                {
                  type: 'value',
                  min: yAxisMin.enabled
                    ? (value: { min: number; max: number; }) => {
                      const computed =
                          value.min -
                          (value.max - value.min) *
                            (yAxisMin.minRatio ?? 0.2);
                      return value.min >= 0
                        ? Math.max(0, computed)
                        : computed;
                    }
                    : yAxisMin.min !== undefined
                      ? yAxisMin.min
                      : null,
                  nameTextStyle: {
                    color: '#838697',
                    fontSize: 12,
                  },
                  splitLine: {
                    lineStyle: {
                      color: '#E8E8E8',
                      type: 'dashed',
                    },
                  },
                  interval: chartConfig?.interval,
                  axisLine: {
                    show: !!chartConfig?.showYAxisLine,
                    lineStyle: {
                      color: 'rgba(0,0,0,0.45)',
                      width: 0.5,
                    },
                  },
                  axisTick: {
                    show: false,
                  },
                  axisLabel: {
                    ...(typeof chartConfig?.yAxisLabelMargin === 'number'
                      ? { margin: chartConfig.yAxisLabelMargin }
                      : {}),
                    fontSize: 12,
                    formatter: (value: number) =>
                      dataFormat({
                        value,
                        // 同上：FormatRule.type 可选，此处结构断言对齐 dataFormat 入参
                        formatRule: chartConfig?.yAxisFormat as unknown as IFormatRule,
                      }) || '',
                  },
                },
              ];
              // 单 X 轴（KPI 横轴事件数字改用拐点 label 实现，参照 daily Engagement CardLine）
              option.xAxis = [
                {
                  type: 'category',
                  data: xAxisData,
                  position: 'bottom',
                  axisTick: false,
                  axisLine: {
                    lineStyle: {
                      width: 0.5,
                    },
                  },
                  axisLabel: {
                    fontSize: 10,
                    color: '#838697',
                    formatter: (value: string) => {
                      const formatRule = chartConfig?.dateFormatRule;
                      return (
                        dateTimeFormat(
                          (value || '') as string,
                          formatRule as string,
                        ) || ''
                      );
                    },
                  },
                },
              ];

              // 添加虚拟 series 用于事件定位
              if (showEventLayer) {
                option.series = [
                  ...(option.series || []),
                  {
                    name: 'TopTipsEvent',
                    type: 'line',
                    stack: 'Total',
                    data: [],
                  },
                ];
              }

              option = addChartLabel(option, {
                formatter: (params: TooltipParams) => {
                  // 排除第一个点的 label 显示，避免与 Y 轴刻度重叠
                  if (params.dataIndex === 0) {
                    return '';
                  }
                  const value =
                    params?.data?.value ?? params?.value ?? params?.data;
                  return (
                    dataFormat({
                      value: value as number,
                      // 同上：FormatRule.type 可选，此处结构断言对齐 dataFormat 入参
                      formatRule: chartConfig?.formatRule as unknown as IFormatRule,
                    }) || ''
                  );
                },
                show: chartConfig?.showDataLabel !== false,
              });

              // 隐藏第一个点的 symbol，避免与 Y 轴刻度重叠；若首点右侧无数据（该线仅首点有值），须保留 symbol
              option.series = option.series?.map((s: EChartsSeries) => {
                if (s.type === 'line' && s.name !== 'TopTipsEvent') {
                  const dataArr = s.data ?? [];
                  const dataLen = dataArr.length;
                  const shouldHideFirstPointSymbol =
                    dataLen > 1 &&
                    !isEmptyLineDataNeighbor(dataArr[0]) &&
                    !isEmptyLineDataNeighbor(dataArr[1]);
                  return {
                    ...s,
                    data: s.data?.map((item, index: number) => {
                      if (index === 0 && shouldHideFirstPointSymbol) {
                        if (
                          typeof item === 'object' &&
                          item !== null &&
                          'value' in item
                        ) {
                          // 判断是否为孤立数据点（后续没有有效数据），不隐藏以确保可见
                          // ECharts series.data 元素可能是原始值或 {value, ...} 对象
                          const hasNextValid = s.data
                            ?.slice(1)
                            .some((d: unknown) => {
                              const v = typeof d === 'object' && d !== null ? (d as { value?: unknown; })?.value : d;
                              return v !== undefined && v !== null && v !== '';
                            });
                          if (!hasNextValid) return item;
                          return {
                            ...item,
                            itemStyle: {
                              ...(item.itemStyle || {}),
                              opacity: 0,
                            },
                          };
                        }
                        return {
                          value: item as number,
                          itemStyle: {
                            opacity: 0,
                          },
                        };
                      }
                      return item;
                    }),
                  };
                }
                return s;
              });

              option = xAxisHighLight(option, {
                formatter: (params: { value: string; }) => {
                  const formatRule = chartConfig?.dateFormatRule;
                  return (
                    dateTimeFormat(
                      (params?.value || '') as string,
                      formatRule as string,
                    ) || ''
                  );
                },
              });

              // 从 series 中提取 legend 数据，过滤 TopTipsEvent 与 KPI 横轴事件 series
              const legendNames = option.series
                ?.filter(
                  (s: EChartsSeries) =>
                    s.id !== 'eventDetails' && s.name !== 'TopTipsEvent',
                )
                .map((s: EChartsSeries) => s.name)
                .filter(
                  (name: string | undefined): name is string => !!name,
                );

              option.legend = {
                ...option.legend,
                data: legendNames,
              };
              applyLegendDimensionIcons(option, {
                lineSeries,
                seriesConfig,
                chartConfig,
              });

              // 禁用 series 的 emphasis 效果，防止悬浮 legend 时显示所有 symbol
              option.series = option.series?.map((s: EChartsSeries) => ({
                ...s,
                emphasis: {
                  disabled: false,
                },
              }));

              // 必须保留 containLabel，否则覆盖 makeupOptions 默认值后 Y 轴刻度易被裁切
              // chartConfig.grid 形态由上游传入，结构宽泛，此处仅读取 top / topNoEvent
              const gridConfig = ((chartConfig as { grid?: Record<string, unknown>; })?.grid || {}) as {
                top?: number;
                topNoEvent?: number;
                [key: string]: unknown;
              };
              const {
                top: configGridTop,
                topNoEvent: configGridTopNoEvent,
                ...restGridConfig
              } = gridConfig;
              const gridBottom = showKpiEventAxisRow ? 36 : 32;
              // ECharts GridComponentOption 支持 containLabel 但 EChartsOption.grid 类型联合较严，按原逻辑以宽泛 Record 承载
              const nextGrid: Record<string, unknown> = {
                containLabel: true,
                top: showEventLayer
                  ? configGridTop || 90
                  : configGridTopNoEvent || 16,
                left: 14,
                right: 38,
                bottom: gridBottom,
                ...restGridConfig,
              };
              option.grid = nextGrid as EChartsOption['grid'];

              /**
               * 关键：KPI 横轴事件 series 通过此处注入（每次 setOption 都跑），
               * 而监听只在 chartEventConfig 第一次 init 时挂；这样事件数据后到时仅触发一次普通 setOption，
               * LineChart 不重挂、不闪空白。
               */
              if (showKpiEventAxisRow) {
                option = kpiEventAppendSeries(option);
              }

              // 全屏时放大 ECharts fontSize；X 轴 / hover 刻度 / tooltip 优先 fullscreenXAxisScale
              const { fsScale, xAxisFsScale } = resolveFullscreenFontScales({
                isFullScreen: viewportFullscreen?.isFullScreen,
                scale: viewportFullscreen?.scale,
                xAxisScale: viewportFullscreen?.xAxisScale,
                chartFullscreenXAxisScale: chartConfig?.fullscreenXAxisScale,
              });
              return applyEchartsOptionFontSizeScale(
                option as Record<string, unknown>,
                fsScale,
                xAxisFsScale,
              ) as EChartsOption;
            }}
          />
          {showKpiEventAxisRow ? (
            <EventDetailsPop
              isOverview
              currentEvents={kpiEventCurrent}
              pathname={eventDetailsPopPathname}
              timeType="Day"
              popRef={kpiEventPopRef}
              style={{ top: '85%' }}
              tagStyleMap={kpiEventTagStyleMap}
            />
          ) : null}
        </div>
      }
    />
  );

  /** 折线/表格视图：标题、副标题、事件开关、Radio、Tab、下载均由 CardBox 统一布局 */
  if (effectiveShowTableView && trendTableModel) {
    return (
      <div
        className={classNames('dbd-event-metric-trend', className, {
          'dbd-event-metric-trend--table-view': showTableView
        })}
        style={style}
        {...restProps}
      >
        <CardBox
          className={classNames(
            'register-table',
            'dbd-event-metric-trend-cardbox',
            'dbd-event-metric-trend-main-card',
          )}
          bordered={false}
          titleCom={
            <div
              ref={headerRef}
              className="dbd-event-metric-trend-cardbox-header-measure"
            >
              <div className="dbd-event-metric-trend-left">
                <div
                  className="dbd-event-metric-trend-title"
                  style={link ? { cursor: 'pointer' } : undefined}
                  onClick={
                    link ? () => window.open(link as string) : undefined
                  }
                >
                  {title}
                </div>
                {link && (
                  <SvgIcon
                    className="dbd-metrics-card-wrap__title__right-icon"
                    type="arrow-right"
                  />
                )}
                {!hideControls && showEventSwitch && (
                  <div className="dbd-event-metric-trend-switch">
                    <Switch
                      value={eventSwitchValue}
                      onChange={handleChartEventSwitchChange}
                    >
                      <span className="dbd-event-metric-trend-switch-text">{getName(isEN, 'Event', '事件')}</span>
                    </Switch>
                  </div>
                )}
                {kpiUi.settings &&
                  !hideControls &&
                  showEventSwitch &&
                  hasRenderableEvents && (
                  <span
                    className="dbd-event-metric-trend-event-setting-icon"
                    onClick={() => setEventSettingVisible(true)}
                    role="presentation"
                  >
                    <SvgIcon className="dbd-event-metric-trend-event-setting-icon-svg" type="jichushezhi" />
                  </span>
                )}
                {!hideControls &&
                  radioConfig &&
                  radioConfig.options?.length > 0 && (
                  <Radio.Group
                    className="dbd-event-metric-trend-radio"
                    value={effectiveRadioValue}
                    onChange={(value) => setSelectedRadioValue(value)}
                  >
                    {radioConfig.options.map((option: RadioOption) => (
                      <Radio key={option.value} name={option.value}>
                        {getName(isEN, option.labelEn, option.labelCn)}
                      </Radio>
                    ))}
                  </Radio.Group>
                )}
                {renderSubTitle()}
              </div>
              {contentHeader ? (
                <div className="dbd-event-metric-trend-cardbox-content-header">
                  {contentHeader as React.ReactNode}
                </div>
              ) : null}
            </div>
          }
          subTitleCom={null}
          filterSlot={null}
          height={tableViewHeight}
          defaultAvtive={0}
          isLoading={false}
          isError={false}
          isNodata={false}
          onChangeChartType={(tab: string) => setViewChartType(tab)}
          renderList={[
            {
              type: 'lineChart',
              svgType: 'discount-chart',
              content: formatMessage({ id: 'common_line_chart' }),
              component: trendBodyChart,
            },
            {
              type: 'sheet',
              svgType: 'sheet',
              content: formatMessage({ id: 'common_table' }),
              component: trendSheetTableContent,
            },
          ]}
          downloadClick={
            hasData
              ? () =>
                // downloadPack 由 useEventMetricTrendTable 生成，运行时结构与 TableOption 兼容
                handleDownload(trendTableModel.downloadPack as Parameters<typeof handleDownload>[0], title)
              : undefined
          }
        />
        {showEventLayer &&
          hasData &&
          !forceNoDataProp &&
          !eventLoading &&
          !loading &&
          !errorMsg &&
          viewChartType === 'lineChart' && (
          <div
            className={classNames('dbd-event-metric-trend-wrapper', {
              'dbd-event-metric-trend-wrapper--no-event-underline':
                chartConfig?.eventBubbleItemUnderline === false,
            })}
            style={{ top: eventWrapperTop }}
          >
            <EventWindows
              key={`${eventResizeKey}-${effectiveRadioValue}`}
              myChart={chartInstance}
              eventList={eventListForKpiEventWindows}
              format={'YYYY-MM-DD'}
              maxTimeValue={xAxisData?.[xAxisData.length - 1]}
              showEventAxis={eventWindowsShowEventAxis}
              dateType={'daily'}
              xAxisData={xAxisData}
              yAxisMin={true}
              eventDetailsNav={eventDetailsNavForWindows}
            />
          </div>
        )}
        {eventSettingModalNode}
      </div>
    );
  }

  return (
    <>
      <MetricsCardWrap
        titleCn={''}
        titleEn={''}
        className={classNames('dbd-event-metric-trend', className, {
          'dbd-event-metric-trend--with-content-header': !!contentHeader,
          'dbd-event-metric-trend--table-view': showTableView,
        })}
        style={style}
        // contentHeader 为上游透传的 React 节点
        contentHeader={contentHeader as React.ReactNode}
        cardHeader={
          <div ref={headerRef} className="dbd-event-metric-trend-header">
            {/* 左侧区域 */}
            <div className="dbd-event-metric-trend-left">
              <div
                className="dbd-event-metric-trend-title"
                style={link ? { cursor: 'pointer' } : {}}
                onClick={link ? () => window.open(link as string) : undefined}
              >
                {title}
              </div>
              {link && (
                <SvgIcon
                  className="dbd-metrics-card-wrap__title__right-icon"
                  type="arrow-right"
                />
              )}

              {/* Event Switch - hideControls 时隐藏 */}
              {!hideControls && showEventSwitch && (
                <div className="dbd-event-metric-trend-switch">
                  <Switch
                    value={eventSwitchValue}
                    onChange={handleChartEventSwitchChange}
                  >
                    {getName(isEN, 'Event', '事件')}
                  </Switch>
                </div>
              )}
              {kpiUi.settings &&
              !hideControls &&
              showEventSwitch &&
              hasRenderableEvents && (
                <span
                  className="dbd-event-metric-trend-event-setting-icon"
                  onClick={() => setEventSettingVisible(true)}
                  role="presentation"
                >
                  <SvgIcon type="jichushezhi" />
                </span>
              )}

              {/* Radio 组件 - hideControls 时隐藏 */}
              {!hideControls &&
              radioConfig &&
              radioConfig.options?.length > 0 && (
                <Radio.Group
                  className="dbd-event-metric-trend-radio"
                  value={effectiveRadioValue}
                  onChange={(value) => setSelectedRadioValue(value)}
                >
                  {radioConfig.options.map((option: RadioOption) => (
                    <Radio key={option.value} name={option.value}>
                      {getName(isEN, option.labelEn, option.labelCn)}
                    </Radio>
                  ))}
                </Radio.Group>
              )}

              {/* 副标题区域：支持 AI 动态副标题和静态副标题 */}
              {renderSubTitle()}
            </div>

            {/* 右侧区域：hideControls 时仍可由 viewportFullscreen 展示全屏入口（与 MultiTrendPanel 组合下载并存） */}
            {(!hideControls || viewportFullscreen?.enabled) && (
              <div className="dbd-event-metric-trend-operation">
                {!hideControls && hasData && (
                  <DownloadButton
                    className="dbd-operation-btn"
                    config={{
                      fileName: downloadConfig?.fileName || title,
                      tabConfig: {
                        columns: downloadColumnsForExcel,
                        dataSource: getDataSource(),
                        tabName: downloadConfig?.sheetName || title,
                      },
                    }}
                  />
                )}
                {viewportFullscreen?.enabled ? (
                  <ViewportFullscreenTip
                    className="dbd-operation-btn dbd-viewport-fullscreen-tip"
                    isFullScreen={viewportFullscreen.isFullScreen}
                    requestFullScreen={viewportFullscreen.requestFullScreen}
                    exitFullScreen={viewportFullscreen.exitFullScreen}
                    disabled={Boolean(loading || eventLoading)}
                  />
                ) : null}
              </div>
            )}
          </div>
        }
        {...restProps}
      >
        {trendBodyChart}
        {showEventLayer &&
        hasData &&
        !forceNoDataProp &&
        !eventLoading &&
        !loading &&
        !errorMsg && (
          <div
            className={classNames('dbd-event-metric-trend-wrapper', {
              'dbd-event-metric-trend-wrapper--no-event-underline':
              chartConfig?.eventBubbleItemUnderline === false,
            })}
            style={{ top: eventWrapperTop }}
          >
            <EventWindows
              key={`${eventResizeKey}-${effectiveRadioValue}`}
              myChart={chartInstance}
              eventList={eventListForKpiEventWindows}
              format={'YYYY-MM-DD'}
              maxTimeValue={xAxisData?.[xAxisData.length - 1]}
              showEventAxis={eventWindowsShowEventAxis}
              dateType={'daily'}
              xAxisData={xAxisData}
              yAxisMin={true}
              eventDetailsNav={eventDetailsNavForWindows}
            />
          </div>
        )}
      </MetricsCardWrap>
      {eventSettingModalNode}
    </>
  );
};

export default EventMetricTrend;
